import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { Search, Filter, Download, ExternalLink, ChevronRight, Check, Globe, Link as LinkIcon, UserCheck, Loader } from 'lucide-react';
import { API_URL as CONFIG_API_URL } from '../../config';

const API_URL = CONFIG_API_URL || 'http://8.141.122.155:3002/api';

export default function SeoTaskResults() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlatforms, setFilterPlatforms] = useState([]);
  const [filterCamps, setFilterCamps] = useState([]);
  const [showPlatformDropdown, setShowPlatformDropdown] = useState(false);
  const [showCampDropdown, setShowCampDropdown] = useState(false);
  
  const platforms = ['百度', '小红书', '抖音', '知乎', '微博', 'B站'];
  const camps = ['本品占位', '竞品拦截', '第三方中立', '负面/吐槽'];

  const filterRef = useRef(null);

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowPlatformDropdown(false);
        setShowCampDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchResults = async () => {
    setLoading(true);
    try {
      const activeProjectId = localStorage.getItem('globalBrand');
      const token = localStorage.getItem('token');
      
      let url = `${API_URL}/results?system=seo&limit=2000`;
      if (activeProjectId && activeProjectId !== 'all' && activeProjectId !== 'undefined') {
        url += `&brandProjectId=${activeProjectId}`;
      }
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        setResults(res.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch SEO results:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResults();
    const handleGlobalFilterChange = () => fetchResults();
    window.addEventListener('globalFilterChange', handleGlobalFilterChange);
    return () => window.removeEventListener('globalFilterChange', handleGlobalFilterChange);
  }, []);

  const parseSources = (str, item = {}) => {
    let parsed = {};
    if (str) {
      try {
        parsed = JSON.parse(str);
      } catch(e) {}
    }
    
    // If it's an array (like Xiaohongshu rawdata), take the first item
    if (Array.isArray(parsed) && parsed.length > 0) {
      parsed = parsed[0];
    } else if (Array.isArray(parsed)) {
      parsed = {};
    }
    
    // Fallback to geoResultSources if available
    if ((!parsed.title && !parsed.media) && item.mediaSources && item.mediaSources.length > 0) {
       parsed.title = item.mediaSources[0].title || item.mediaSources[0].mediaName || '';
       parsed.media = item.mediaSources[0].mediaName || item.mediaSources[0].name || '';
       parsed.link = item.mediaSources[0].url || '';
    }
    
    return parsed || {};
  };

  const getCampBySentiment = (sentiment) => {
    if (sentiment === 'positive') return '本品占位';
    if (sentiment === 'negative') return '负面/吐槽';
    return '第三方中立';
  };

  const handleExport = () => {
    const exportData = filteredResults.map(item => {
      const sources = parseSources(item.sources, item);
      const camp = getCampBySentiment(item.sentiment);
      const rawRank = sources.position || (item.isTop1 ? 1 : (item.isTop3 ? 3 : 0));
      let pageNum = 1;
      let rankOnPage = rawRank;
      if (rawRank > 0) {
         pageNum = Math.ceil(rawRank / 10);
         rankOnPage = ((rawRank - 1) % 10) + 1;
      } else {
         rankOnPage = '-';
         pageNum = '-';
      }
      
      return {
        '搜索平台': item.engineName,
        '搜索词': item.question,
        '页码': pageNum !== '-' ? `第 ${pageNum} 页` : '-',
        '排名位置': rankOnPage !== '-' ? `第 ${rankOnPage} 名` : '-',
        '综合排名': rawRank !== 0 ? rawRank : '-',
        '占位标题': sources.title || '',
        '媒体阵地': sources.media || '',
        '阵营判定': camp,
        '创建时间': new Date(item.createdAt).toLocaleString()
      };
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "搜索溯源明细");
    XLSX.writeFile(wb, `SEO搜索溯源明细_${new Date().getTime()}.xlsx`);
  };

  const filteredResults = results.filter(item => {
    const sources = parseSources(item.sources, item);
    const camp = getCampBySentiment(item.sentiment);
    const title = sources.title || '';
    const media = sources.media || '';
    
    const matchSearch = item.question?.includes(searchTerm) || title.includes(searchTerm) || media.includes(searchTerm);
    const matchPlatform = filterPlatforms.length === 0 || filterPlatforms.includes(item.engineName);
    const matchCamp = filterCamps.length === 0 || filterCamps.includes(camp);
    
    return matchSearch && matchPlatform && matchCamp;
  });

  const getCampStyle = (camp) => {
    switch(camp) {
      case '本品占位': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800';
      case '竞品拦截': return 'bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-400 border-rose-200 dark:border-rose-800';
      case '第三方中立': return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700';
      case '负面/吐槽': return 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400 border-red-200 dark:border-red-800';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 pb-12">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex justify-between items-end">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <Search className="w-6 h-6 mr-3 text-[#2996cf]" />
              SEO 搜索溯源明细
            </h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              逐条回溯各平台关键词搜索结果（SERP），分析霸屏占位情况与竞品拦截防线。
            </p>
          </div>
          <button
              onClick={handleExport}
              className="flex items-center px-4 py-2 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg text-sm font-medium hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors border border-emerald-200 dark:border-emerald-800/50"
            >
              <Download className="w-4 h-4 mr-2" />
              导出报表
            </button>
        </div>

        <div className="bg-white dark:bg-slate-800/50 backdrop-blur-xl rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden flex flex-col h-[calc(100vh-200px)]">
          {/* Toolbar */}
          <div className="p-4 border-b border-gray-200 dark:border-slate-700 flex flex-wrap gap-4 items-center justify-between bg-slate-50/50 dark:bg-slate-800/50">
            <div className="flex flex-wrap gap-3 items-center" ref={filterRef}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="搜索关键词、标题或媒体..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2996cf] text-gray-900 dark:text-white w-64"
                />
              </div>

              {/* Platform Filter */}
              <div className="relative filter-dropdown">
                <button
                  onClick={() => setShowPlatformDropdown(!showPlatformDropdown)}
                  className="flex items-center px-3 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-slate-800 text-gray-700 dark:text-slate-200"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  平台 {filterPlatforms.length > 0 && `(${filterPlatforms.length})`}
                </button>
                {showPlatformDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg z-20 py-1">
                    {platforms.map(p => (
                      <label key={p} className="flex items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-[#2996cf] focus:ring-[#2996cf] mr-2"
                          checked={filterPlatforms.includes(p)}
                          onChange={(e) => {
                            if (e.target.checked) setFilterPlatforms([...filterPlatforms, p]);
                            else setFilterPlatforms(filterPlatforms.filter(x => x !== p));
                          }}
                        />
                        <span className="text-sm text-gray-700 dark:text-slate-200">{p}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Camp Filter */}
              <div className="relative filter-dropdown">
                <button
                  onClick={() => setShowCampDropdown(!showCampDropdown)}
                  className="flex items-center px-3 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-slate-800 text-gray-700 dark:text-slate-200"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  阵营 {filterCamps.length > 0 && `(${filterCamps.length})`}
                </button>
                {showCampDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg z-20 py-1">
                    {camps.map(c => (
                      <label key={c} className="flex items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-[#2996cf] focus:ring-[#2996cf] mr-2"
                          checked={filterCamps.includes(c)}
                          onChange={(e) => {
                            if (e.target.checked) setFilterCamps([...filterCamps, c]);
                            else setFilterCamps(filterCamps.filter(x => x !== c));
                          }}
                        />
                        <span className="text-sm text-gray-700 dark:text-slate-200">{c}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="text-sm text-slate-500 dark:text-slate-400">
               共计: <strong className="text-slate-700 dark:text-slate-200">{filteredResults.length}</strong> 条记录
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto">
            {loading ? (
               <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                 <Loader className="w-8 h-8 animate-spin mb-4 text-[#2996cf]" />
                 加载数据中...
               </div>
            ) : (
            <table className="w-full text-left border-collapse min-w-[800px] table-fixed">
              <thead className="sticky top-0 bg-gray-50 dark:bg-slate-800/90 backdrop-blur z-10 shadow-sm">
                <tr>
                  <th className="py-3 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-slate-700 w-[10%]">搜索平台</th>
                  <th className="py-3 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-slate-700 w-[14%]">搜索词 / 维度</th>
                  <th className="py-3 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-slate-700 w-[8%]">页码</th>
                  <th className="py-3 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-slate-700 w-[8%]">排名</th>
                  <th className="py-3 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-slate-700 w-[30%]">占位标题 / 摘要</th>
                  <th className="py-3 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-slate-700 w-[12%]">媒体阵地 / 账号</th>
                  <th className="py-3 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-slate-700 w-[10%]">阵营判定</th>
                  <th className="py-3 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-slate-700 text-right w-[8%]">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700/50 bg-white dark:bg-transparent">
                {filteredResults.map((item) => {
                  const sources = parseSources(item.sources, item);
                  const camp = getCampBySentiment(item.sentiment);
                  const rawRank = sources.position || (item.isTop1 ? 1 : (item.isTop3 ? 3 : 0));
                  let pageNum = 1;
                  let rankOnPage = rawRank;
                  if (rawRank > 0) {
                     pageNum = Math.ceil(rawRank / 10);
                     rankOnPage = ((rawRank - 1) % 10) + 1;
                  } else {
                     rankOnPage = '-';
                     pageNum = '-';
                  }
                  const title = sources.title || '';
                  const media = sources.media || '';
                  const link = sources.link || '#';
                  
                  return (
                    <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors group">
                      <td className="py-3 px-2 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-1 rounded bg-slate-100 dark:bg-slate-800 text-xs font-medium text-slate-600 dark:text-slate-300">
                          {item.engineName?.includes('百度') ? <Globe className="w-3 h-3 mr-1" /> : <UserCheck className="w-3 h-3 mr-1" />}
                          {item.engineName}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-sm font-medium text-gray-900 dark:text-slate-200 truncate" title={item.question}>
                        {item.question}
                      </td>
                      <td className="py-3 px-2 whitespace-nowrap">
                        <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                          {pageNum !== '-' ? `第 ${pageNum} 页` : '-'}
                        </span>
                      </td>
                      <td className="py-3 px-2">
                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md font-bold text-xs ${
                          rankOnPage === 1 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400' :
                          rankOnPage <= 3 ? 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300' :
                          'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                        }`}>
                          {rankOnPage}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-sm text-gray-800 dark:text-slate-200 font-medium">
                        <div className="line-clamp-2" title={title}>
                          {title}
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center text-sm text-slate-500 dark:text-slate-400 truncate" title={media}>
                          <LinkIcon className="w-3.5 h-3.5 mr-1.5 shrink-0" />
                          <span className="truncate">{media}</span>
                        </div>
                      </td>
                      <td className="py-3 px-2 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded border text-xs font-medium whitespace-nowrap ${getCampStyle(camp)}`}>
                          {camp}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right">
                        {link && link !== '#' && (
                          <a href={link} target="_blank" rel="noopener noreferrer" className="p-1.5 text-slate-400 hover:text-[#2996cf] transition-colors inline-block" title="查看原始网页">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filteredResults.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-12 text-center text-gray-500 dark:text-slate-400">
                      没有找到匹配的溯源记录
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
